import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import {
  getOpportunityById,
  getRelatedAwards,
  formatCurrency,
  formatDate,
  formatDeadline,
} from "../services/opportunitiesService";
import {
  ArrowLeft,
  Building2,
  Calendar,
  DollarSign,
  FileText,
  Tag,
  TrendingUp,
  TrendingDown,
  Minus,
  ExternalLink,
} from "lucide-react";

const SIMILARITY_BAND_CONFIG = {
  high: { variant: "success", label: "High Match", icon: TrendingUp, color: "text-green-600" },
  medium: { variant: "warning", label: "Medium Match", icon: Minus, color: "text-yellow-600" },
  low: { variant: "secondary", label: "Low Match", icon: TrendingDown, color: "text-gray-600" },
  none: { variant: "outline", label: "No Match", icon: null, color: "text-gray-400" },
};

export default function OpportunityDetail() {
  const { id } = useParams();
  const [opportunity, setOpportunity] = useState(null);
  const [relatedAwards, setRelatedAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        const opp = await getOpportunityById(id);
        if (!opp) {
          setError("Opportunity not found");
          return;
        }

        setOpportunity(opp);

        // Fetch related historical awards
        const awards = await getRelatedAwards(id);
        setRelatedAwards(awards);
      } catch (err) {
        console.error("Error fetching opportunity:", err);
        setError("Failed to load opportunity details");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [id]);

  const renderSimilarityBand = (band) => {
    const config = SIMILARITY_BAND_CONFIG[band] || SIMILARITY_BAND_CONFIG.none;
    const Icon = config.icon;
    return (
      <div className="flex items-center gap-2">
        <Badge variant={config.variant} className="gap-1">
          {Icon && <Icon className="h-3 w-3" />}
          {config.label}
        </Badge>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading opportunity details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link to="/contracts">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Opportunities
          </Button>
        </Link>
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            {error}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Link to="/contracts">
            <Button variant="ghost" size="sm" className="-ml-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Opportunities
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">
              {opportunity.summary_title || "Untitled Opportunity"}
            </h1>
            {opportunity.is_new && <Badge>NEW</Badge>}
          </div>
          <p className="text-muted-foreground">
            Opportunity ID: {opportunity.id}
          </p>
        </div>
        {renderSimilarityBand(opportunity.similarity_band)}
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Main Details */}
        <div className="md:col-span-2 space-y-6">
          {/* Core Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Opportunity Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Tag className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">NAICS Code</p>
                    <p className="font-medium">
                      <code className="bg-muted px-1 py-0.5 rounded">
                        {opportunity.naics_code || "N/A"}
                      </code>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">PSC Code</p>
                    <p className="font-medium">
                      <code className="bg-muted px-1 py-0.5 rounded">
                        {opportunity.psc_code || "N/A"}
                      </code>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Agency</p>
                    <p className="font-medium">{opportunity.agency_name || "N/A"}</p>
                    {opportunity.office_name && (
                      <p className="text-sm text-muted-foreground">
                        {opportunity.office_name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Effective Date</p>
                    <p className="font-medium">
                      {formatDate(opportunity.effective_date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Estimated Value</p>
                    <p className="font-medium font-mono">
                      {formatCurrency(opportunity.estimated_value)}
                    </p>
                  </div>
                </div>

                {opportunity.response_deadline && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Response Deadline</p>
                      {(() => {
                        const deadline = formatDeadline(opportunity.response_deadline);
                        return (
                          <p className={`font-medium ${deadline.urgent ? 'text-red-600' : deadline.closed ? 'text-muted-foreground' : ''}`}>
                            {deadline.text}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm">
                {opportunity.summary_description ||
                 opportunity.description ||
                 "No description available."}
              </p>
            </CardContent>
          </Card>

          {/* SAM.gov Details */}
          {(opportunity.type || opportunity.set_aside || opportunity.notice_id) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">SAM.gov Details</CardTitle>
                <CardDescription>
                  Source data from SAM.gov
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  {opportunity.notice_id && (
                    <div>
                      <p className="text-sm text-muted-foreground">Notice ID</p>
                      <p className="font-mono text-sm">{opportunity.notice_id}</p>
                    </div>
                  )}
                  {opportunity.type && (
                    <div>
                      <p className="text-sm text-muted-foreground">Opportunity Type</p>
                      <p className="font-medium">{opportunity.type}</p>
                    </div>
                  )}
                  {opportunity.set_aside && (
                    <div>
                      <p className="text-sm text-muted-foreground">Set-Aside</p>
                      <p className="font-medium">{opportunity.set_aside}</p>
                    </div>
                  )}
                  {opportunity.sam_url && (
                    <div className="sm:col-span-2">
                      <p className="text-sm text-muted-foreground">SAM.gov Link</p>
                      <a
                        href={opportunity.sam_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        View on SAM.gov
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Related Awards */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Related Historical Awards</CardTitle>
              <CardDescription>
                Past contracts with similar characteristics (from USAspending)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {relatedAwards && relatedAwards.length > 0 ? (
                <div className="space-y-4">
                  {relatedAwards.map((award, index) => (
                    <div
                      key={award.award_piid || award.award_id || index}
                      className="p-3 border rounded-lg space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <Badge
                          variant={
                            award.score >= 0.7
                              ? "success"
                              : award.score >= 0.4
                              ? "warning"
                              : "secondary"
                          }
                        >
                          {Math.round((award.score || 0) * 100)}% match
                        </Badge>
                        {award.band && (
                          <span className="text-xs text-muted-foreground capitalize">
                            {award.band}
                          </span>
                        )}
                      </div>
                      {(award.piid || award.award_id) && (
                        <p className="text-xs font-mono text-muted-foreground">
                          PIID: {award.piid || award.award_id}
                        </p>
                      )}
                      {award.vendor_name && (
                        <p className="text-sm font-semibold text-primary">
                          {award.vendor_name}
                        </p>
                      )}
                      <p className="text-sm">
                        {award.agency_name || "Unknown Agency"}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-sm font-medium">
                          {formatCurrency(award.award_amount)}
                        </p>
                        {award.naics_code && (
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {award.naics_code}
                          </code>
                        )}
                      </div>
                      {award.matchReasons && award.matchReasons.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {award.matchReasons.slice(0, 3).map((reason, i) => (
                            <span key={i} className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                              {reason}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* FPDS Enriched Details */}
                      {award.fpds_detail && (
                        <div className="mt-2 pt-2 border-t border-dashed space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">FPDS Details</p>
                          {award.fpds_detail.vendor_cage && (
                            <p className="text-xs">
                              <span className="text-muted-foreground">CAGE:</span>{" "}
                              <code className="bg-muted px-1 rounded">{award.fpds_detail.vendor_cage}</code>
                            </p>
                          )}
                          {award.fpds_detail.contracting_office_name && (
                            <p className="text-xs">
                              <span className="text-muted-foreground">Office:</span>{" "}
                              {award.fpds_detail.contracting_office_name}
                            </p>
                          )}
                          {award.fpds_detail.modification_count > 0 && (
                            <p className="text-xs">
                              <span className="text-muted-foreground">Modifications:</span>{" "}
                              {award.fpds_detail.modification_count}
                            </p>
                          )}
                          {award.fpds_detail.pricing_type && (
                            <p className="text-xs">
                              <span className="text-muted-foreground">Pricing:</span>{" "}
                              {award.fpds_detail.pricing_type}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No related historical awards found for this opportunity.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Record Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {opportunity.created_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{formatDate(opportunity.created_at)}</span>
                </div>
              )}
              {opportunity.updated_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated</span>
                  <span>{formatDate(opportunity.updated_at)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
